import { createContext } from '@shared/utils/createContext';
import PrintPriceCalculator from '@shared/utils/print-price/PrintPriceCalculator';
import { StationeryProduct } from '@shared/utils/print-price/types';
import React, { ReactNode, useRef } from 'react';
import { useProductToUIMapping } from '../useProductToUIMapping';
import { Product } from '../product.types';
import { useProductAttributeStates } from './state/useProductAttributeStates';
import { useShippingMethodLabelsAndDescriptions } from '@apps/card/hooks/useShippingMethodLabelsAndDescriptions';

type StationeryProductPricingContextType = {
  products: StationeryProduct[];
  activeProduct: StationeryProduct & Product;
  priceCalculator: PrintPriceCalculator;
  attributeStates: ReturnType<typeof useProductAttributeStates>;
};

const [Provider, useStationeryProductPricingContext] = createContext<StationeryProductPricingContextType>({
  name: 'StationeryProductPricingContext',
});

type StationeryProductPricingContextProviderProps = {
  products: StationeryProduct[];
  activeProductId: string;
  children: ReactNode;
};

const StationeryProductPricingContextProvider = (props: StationeryProductPricingContextProviderProps) => {
  const { products, children, activeProductId } = props;
  const { getProductWithUIMapping } = useProductToUIMapping();
  const shippingMethodLabelsAndDescriptions = useShippingMethodLabelsAndDescriptions();
  const priceCalculator = new PrintPriceCalculator(products, shippingMethodLabelsAndDescriptions);
  const activeProduct = products.find(p => p.id === activeProductId);
  const attributeStates = useProductAttributeStates();
  const previousActiveProduct = useRef(activeProductId);

  // Destroy Attribute State to Draft State connections when switching products
  if (activeProductId !== previousActiveProduct.current) {
    previousActiveProduct.current = activeProductId;
    attributeStates.destroyLinks();
  }

  if (!activeProduct) {
    throw new Error('Product not avaliable');
  }
  const mappedActiveProduct = getProductWithUIMapping(activeProduct);

  if (!mappedActiveProduct) {
    throw new Error('Product config not defined');
  }

  return <Provider value={{ products, activeProduct: mappedActiveProduct, priceCalculator, attributeStates }}>{children}</Provider>;
};

export { StationeryProductPricingContextProvider, useStationeryProductPricingContext };
